/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';

export type StatusBadgeTone =
  | 'neutral'
  | 'brand'
  | 'success'
  | 'warning'
  | 'error'
  | 'info';

interface StatusBadgeProps {
  children: React.ReactNode;
  tone?: StatusBadgeTone;
  className?: string;
}

/**
 * Token-driven tone classes for the badge. Each tone reuses the design-system
 * UX tokens (status colours, surface, text) so colour usage stays consistent.
 */
const TONE_CLASSES: Record<StatusBadgeTone, string> = {
  neutral:
    'bg-[color-mix(in_srgb,var(--text-tertiary)_12%,var(--surface-primary))] text-[var(--text-secondary)]',
  brand:
    'bg-[color-mix(in_srgb,var(--seed-base)_12%,var(--surface-primary))] text-[var(--seed-base)]',
  success:
    'bg-[color-mix(in_srgb,var(--status-success)_12%,var(--surface-primary))] text-[var(--status-success-text)]',
  warning:
    'bg-[color-mix(in_srgb,var(--status-warning)_12%,var(--surface-primary))] text-[var(--status-warning-text)]',
  error:
    'bg-[color-mix(in_srgb,var(--status-error)_12%,var(--surface-primary))] text-[var(--status-error-text)]',
  info:
    'bg-[color-mix(in_srgb,var(--status-info)_12%,var(--surface-primary))] text-[var(--status-info-text)]',
};

/**
 * Shared structural classes that guarantee every badge renders at the exact
 * same height. Height is derived purely from UX tokens (text-meta + space
 * paddings + leading-none) so tone never changes the badge box.
 */
const BASE_CLASSES =
  'inline-flex items-center justify-center leading-none whitespace-nowrap ' +
  'text-[length:var(--text-meta)] [font-weight:var(--font-semibold)] ' +
  'px-[var(--space-2)] py-[var(--space-1)] rounded-(--radius-sm)';

/**
 * StatusBadge — the canonical design-system pill/badge.
 *
 * All badges share one height because the box is built only from UX tokens
 * (font size, line-height, vertical/horizontal space tokens, radius token).
 * Use the semantic `tone` to colour the badge; pass `className` only to colour
 * a tone the token palette does not yet cover.
 *
 * @param props - Badge content, semantic tone, and optional className override.
 * @returns A token-driven badge element with a consistent height.
 */
const StatusBadge = ({ children, tone = 'neutral', className }: StatusBadgeProps) => (
  <span className={`${BASE_CLASSES} ${className ?? TONE_CLASSES[tone]}`}>{children}</span>
);

export default StatusBadge;
